<template>
  <div class="seller-analytics p-4">
    <!-- Header & Filters -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <h2 class="text-xl font-bold text-gray-800 mb-4 flex items-center">
        <i class="fas fa-chart-bar mr-2"></i>
        Analytics Data Seller
      </h2>
      <div v-if="idWharhouse" class="flex items-center mb-2">
        <img 
          v-if="idWharhouse.flag" 
          :src="idWharhouse.flag" 
          :alt="idWharhouse.name"
          class="w-7 h-5 object-cover rounded shadow-sm mr-2"
        />
        <div class="flex flex-col">
          <span class="text-blue-700 font-semibold text-sm">{{ idWharhouse.name }}</span>
          <span v-if="idWharhouse.currency" class="text-xs text-gray-500">{{ idWharhouse.currency }}</span>
        </div>
      </div>
      
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Seller Select (OPTIONAL) -->
        <div class="w-full md:w-4/12">
          <label class="block text-sm font-medium text-gray-700 mb-1">Seller</label>
          <v-select
            v-model="filters.seller"
            @search="getUsersDebance($event,'shipsen')"
            :options="sellers"
            :reduce="seller => seller._id"
            label="fullName"
            placeholder="All Sellers"
            class="w-full border border-gray-400 rounded-md"
          ></v-select>
        </div>

        <!-- Date Range -->
        <div class="w-full md:w-4/12">
          <label class="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <date-picker
            v-model="filters.dateRange"
            @change="onDateRangeChange"
            format="YYYY-MM-DD"
            placeholder="Select date range"
            class="w-full-imp rounded-md"
            range
          ></date-picker>
        </div>

        <!-- Search Button -->
        <div class="w-full md:w-3/12">
          <button 
            @click="fetchAnalytics"
            :disabled="loading"
            class="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg"
          >
            <i class="fas fa-search mr-2"></i>
            {{ loading ? 'Loading...' : 'Search' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-10">
      <i class="fas fa-spinner fa-spin fa-3x text-blue-500"></i>
      <p class="mt-2 text-gray-500">Loading analytics data...</p>
    </div>

    <!-- Analytics Data -->
    <div v-else-if="analyticsData">
      
      <!-- Global Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600">
              <i class="fas fa-users fa-2x"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500 text-sm">Total Sellers</p>
              <p class="text-2xl font-bold">{{ analyticsData.global.totalSellers }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600">
              <i class="fas fa-shopping-cart fa-2x"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500 text-sm">Total Orders</p>
              <p class="text-2xl font-bold">{{ analyticsData.global.totalOrders }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-purple-100 text-purple-600">
              <i class="fas fa-percentage fa-2x"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500 text-sm">Avg Confirmation Rate</p>
              <p class="text-2xl font-bold">{{ analyticsData.global.avgTauxConfirmation }}%</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Data Table -->
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-semibold">
            <i class="fas fa-table mr-2"></i>Sellers Analytics (By Product)
          </h3>
          <button 
            @click="exportToExcel"
            class="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded"
          >
            <i class="fas fa-file-excel mr-1"></i>Export Excel
          </button>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr>
                <!-- Seller & Products -->
                <th class="header-main border-r border-gray-600" rowspan="2">SELLER</th>
                <th class="header-main border-r border-gray-600" rowspan="2">PRODUCT</th>
                <th class="header-main border-r border-gray-600" rowspan="2">QTY</th>
                
                <!-- Confirmation Section -->
                <th class="header-confirmation border-r border-b-2 border-blue-400 text-center" colspan="8">
                  <i class="fas fa-phone-alt mr-2"></i>CONFIRMATION
                </th>
                
                <!-- Shipping Section -->
                <th class="header-shipping border-b-2 border-green-400 text-center" colspan="10">
                  <i class="fas fa-truck mr-2"></i>SHIPPING
                </th>
              </tr>
              <tr>
                <!-- Confirmation sub-headers -->
                <th class="header-sub-conf section-confirmation">PENDING</th>
                <th class="header-sub-conf">WAITING</th>
                <th class="header-sub-conf">DÉPOT</th>
                <th class="header-sub-conf">UNREACHED</th>
                <th class="header-sub-conf">EXPIRED</th>
                <th class="header-sub-conf">CONFIRMED</th>
                <th class="header-sub-conf">CANCELLED</th>
                <th class="header-sub-conf border-r border-gray-300">TAUX</th>
                
                <!-- Shipping sub-headers -->
                <th class="header-sub-ship section-shipping">QUEUED</th>
                <th class="header-sub-ship">PREPARED</th>
                <th class="header-sub-ship">SHIPPED</th>
                <th class="header-sub-ship">REPROG.</th>
                <th class="header-sub-ship">REFUNDED</th>
                <th class="header-sub-ship">DELIVERED</th>
                <th class="header-sub-ship">CANCELLED</th>
                <th class="header-sub-ship">TAUX</th>
                <th class="header-sub-ship bg-green-200">TAUX/LEAD</th> 
                <th class="header-sub-ship">TOTAL</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <template v-for="seller in analyticsData.sellers">
                <!-- Product rows for each seller -->
                <tr 
                  v-for="(product, pIndex) in (seller.products.length > 0 ? seller.products : [{ productName: '-', isEmpty: true }])" 
                  :key="`${seller.sellerId}-${pIndex}`"
                  class="hover:bg-gray-50"
                  :class="{ 'border-t-2 border-gray-300': pIndex === 0 }"
                >
                  <!-- Seller name (rowspan for all products) -->
                  <td 
                    v-if="pIndex === 0" 
                    :rowspan="Math.max(seller.products.length, 1)"
                    class="px-3 py-2 font-medium border-r align-top bg-gray-50"
                  >
                    <div class="flex flex-col">
                      <span class="font-semibold text-gray-800">{{ seller.sellerName }}</span>
                      <span class="text-xs text-gray-400">{{ seller.sellerEmail }}</span>
                      <div class="mt-2 text-xs">
                        <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {{ seller.totalOrders }} orders
                        </span>
                      </div>
                    </div>
                  </td>
                  
                  <!-- Product Name -->
                  <td class="px-3 py-2 border-r">
                    <div v-if="!product.isEmpty" class="flex items-center">
                      <img   
                        :src="product.productImage || 'https://img.icons8.com/ios-glyphs/30/no-image.png'" 
                        class="w-8 h-8 object-cover rounded mr-2"
                      />
                      <span class="font-medium">{{ product.productName }}</span>
                    </div>
                  </td>
                  
                  <!-- Quantity -->
                  <td class="px-3 py-2 text-center border-r font-semibold">
                    <span v-if="!product.isEmpty" class="bg-gray-100 px-2 py-1 rounded">
                      {{ product.totalQuantity }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  
                  <!-- Confirmation Stats per Product -->
                  <template v-if="!product.isEmpty">
                    <td class="px-2 py-2 text-center section-confirmation" :class="product.confirmation.pending > 0 ? 'cell-pending' : ''">
                      <span class="value-badge" :class="{'value-zero': product.confirmation.pending === 0}">
                        {{ product.confirmation.pending }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center" :class="product.confirmation.waiting > 0 ? 'cell-waiting' : ''">
                      <span class="value-badge" :class="{'value-zero': product.confirmation.waiting === 0}">
                        {{ product.confirmation.waiting }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center">
                      <span class="value-badge" :class="{'value-zero': product.confirmation.depot === 0}">
                        {{ product.confirmation.depot }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center">
                      <span class="value-badge" :class="{'value-zero': product.confirmation.unreached === 0}">
                        {{ product.confirmation.unreached }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center">
                      <span class="value-badge" :class="{'value-zero': product.confirmation.expired === 0}">
                        {{ product.confirmation.expired }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center cell-confirmed">
                      <button 
                        @click="showOrderDetails(seller.sellerId, product.productId, 'confirmed')"
                        class="value-badge stat-clickable"
                        :class="{ 'has-value': product.confirmation.confirmed > 0 }"
                        title="📋 Click to view confirmed orders"
                      >
                        {{ product.confirmation.confirmed }}
                      </button>
                    </td>
                    <td class="px-2 py-2 text-center" :class="product.confirmation.cancelled > 0 ? 'cell-cancelled' : ''">
                      <button 
                        @click="showOrderDetails(seller.sellerId, product.productId, 'cancelled')"
                        class="value-badge stat-clickable"
                        :class="{'value-zero': product.confirmation.cancelled === 0}"
                         title="📋 Click to view cancelled orders"
                      >
                        {{ product.confirmation.cancelled }}
                      </button>
                    </td>
                    <td class="px-2 py-2 text-center cell-taux border-r border-gray-200">
                      <span 
                        class="value-badge px-2 py-1 rounded-full text-xs"
                        :class="getTauxClass(product.confirmation.tauxConfirmation)"
                      >
                        {{ product.confirmation.tauxConfirmation }}%
                      </span>
                    </td>
                    
                    <!-- Shipping Stats per Product -->
                    <td class="px-2 py-2 text-center section-shipping">
                      <span class="value-badge" :class="{'value-zero': product.shipping.queued === 0}">
                        {{ product.shipping.queued }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center" :class="product.shipping.prepared > 0 ? 'cell-shipped' : ''">
                      <span class="value-badge">{{ product.shipping.prepared }}</span>
                    </td>
                    <td class="px-2 py-2 text-center" :class="product.shipping.shipped > 0 ? 'cell-shipped' : ''">
                      <span class="value-badge">{{ product.shipping.shipped }}</span>
                    </td>
                    <td class="px-2 py-2 text-center">
                      <span class="value-badge" :class="{'value-zero': product.shipping.reprogrammed === 0}">
                        {{ product.shipping.reprogrammed }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center">
                      <span class="value-badge" :class="{'value-zero': product.shipping.refunded === 0}">
                        {{ product.shipping.refunded }}
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center cell-delivered">
                      <button 
                        @click="showShippingDetails(seller.sellerId, product.productId, 'delivered')"
                        class="value-badge stat-clickable"
                        :class="{ 'has-value': product.shipping.delivered > 0 }"
                        title="📋 Click to view delivered shipping"
                      >
                        {{ product.shipping.delivered }}
                      </button>
                    </td>
                    <td class="px-2 py-2 text-center" :class="product.shipping.cancelled > 0 ? 'cell-cancelled' : ''">
                      <span class="value-badge">{{ product.shipping.cancelled }}</span>
                    </td>
                    <td class="px-2 py-2 text-center cell-taux">
                      <span 
                        class="value-badge px-2 py-1 rounded-full text-xs"
                        :class="getTauxClass(product.shipping.tauxLivraison)"
                      >
                        {{ product.shipping.tauxLivraison }}%
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center cell-taux-lead">
                      <span 
                        class="value-badge px-2 py-1 rounded-full text-xs"
                        :class="getTauxClass(product.shipping.tauxPerLead)"
                      >
                        {{ product.shipping.tauxPerLead }}%
                      </span>
                    </td>
                    <td class="px-2 py-2 text-center bg-purple-50">
                      <button 
                        @click="showShippingDetails(seller.sellerId, product.productId)"
                        class="value-badge stat-clickable"
                        :class="{ 'has-value': product.shipping.totalShipped > 0 }"
                        title="📋 Click to view all shipping"
                      >
                        {{ product.shipping.totalShipped }}
                      </button>
                    </td>
                  </template>
                  
                  <!-- Empty product row -->
                  <template v-else>
                    <td colspan="17" class="px-2 py-2 text-center text-gray-400">
                      No products
                    </td>
                  </template>
                </tr>
                
                <!-- Seller Summary Row -->
                <tr 
                  v-if="seller.products.length > 1"
                  :key="`${seller.sellerId}-summary`"
                  class="bg-blue-50 font-semibold border-t-2 border-blue-200 summaryseller"
                >
                  <td class="px-3 py-2 border-r" colspan="2">
                    <span class="text-white">
                      <i class="fas fa-calculator mr-1"></i>
                      {{ seller.sellerName }} - TOTAL
                    </span>
                  </td>
                  <td class="px-3 py-2 text-center border-r">
                    {{ seller.products.reduce((sum, p) => sum + p.totalQuantity, 0) }}
                  </td>
                  
                  <!-- Confirmation Totals -->
                  <td class="px-2 py-2 text-center section-confirmation">{{ seller.totals.confirmation.pending }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.confirmation.waiting }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.confirmation.depot }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.confirmation.unreached }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.confirmation.expired }}</td>
                  <td class="px-2 py-2 text-center cell-confirmed">{{ seller.totals.confirmation.confirmed }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.confirmation.cancelled }}</td>
                  <td class="px-2 py-2 text-center border-r border-gray-200">
                    <span :class="getTauxClass(seller.totals.confirmation.tauxConfirmation)" class="px-2 py-1 rounded-full text-xs">
                      {{ seller.totals.confirmation.tauxConfirmation }}%
                    </span>
                  </td>
                  
                  <!-- Shipping Totals -->
                  <td class="px-2 py-2 text-center section-shipping">{{ seller.totals.shipping.queued }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.shipping.prepared }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.shipping.shipped }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.shipping.reprogrammed }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.shipping.refunded }}</td>
                  <td class="px-2 py-2 text-center cell-delivered">{{ seller.totals.shipping.delivered }}</td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.shipping.cancelled }}</td>
                  <td class="px-2 py-2 text-center">
                    <span :class="getTauxClass(seller.totals.shipping.tauxLivraison)" class="px-2 py-1 rounded-full text-xs">
                      {{ seller.totals.shipping.tauxLivraison }}%
                    </span>
                  </td>
                   <td class="px-2 py-2 text-center">
                    <span :class="getTauxClass(seller.totals.shipping.tauxPerLead)" class="px-2 py-1 rounded-full text-xs">
                      {{ seller.totals.shipping.tauxPerLead }}%
                    </span>
                  </td>
                  <td class="px-2 py-2 text-center">{{ seller.totals.shipping.totalShipped }}</td>
                </tr>
              </template>
            </tbody>
             <tfoot class="bg-gray-800 text-white font-bold">
                <tr>
                  <td colspan="2" class="px-3 py-3 text-center">GRAND TOTAL</td>
                  <td class="px-3 py-3 text-center">{{ grandTotals.qty }}</td>
                  <!-- Confirmation -->
                  <td class="px-2 py-3 text-center">{{ grandTotals.pending }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.waiting }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.depot }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.unreached }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.expired }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.confirmed }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.cancelled }}</td>
                  <td class="px-2 py-3 text-center bg-blue-900">{{ grandTotals.tauxConf }}%</td>
                  <!-- Shipping -->
                  <td class="px-2 py-3 text-center">{{ grandTotals.queued }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.prepared }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.shipped }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.reprogrammed }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.refunded }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.delivered }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.shipCancelled }}</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.tauxShip }}%</td>
                  <td class="px-2 py-3 text-center bg-green-900">{{ grandTotals.tauxLead }}%</td>
                  <td class="px-2 py-3 text-center">{{ grandTotals.totalShipped }}</td>
                </tr>
              </tfoot>
          </table>
        </div>
        
        <div v-if="analyticsData.sellers.length <= 0" class="bg-blue-100 py-8 relative">
          <span class="w-full flex justify-center text-center py-3 border-b border-gray-200 text-base">
            <p class="font-semibold flex text-blue-500 whitespace-no-wrap">
              <i class="material-icons mx-3">error</i>
              <span>No Data Found</span>   
            </p>
          </span>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="!loading && filters.warehouse" class="text-center py-10 bg-white rounded-lg shadow">
      <i class="fas fa-inbox fa-3x text-gray-300 mb-4"></i>
      <p class="text-gray-500">Click "Search" to load analytics data</p>
    </div>

    <!-- Order Details Modal -->
    <div 
      v-if="showDetailsModal" 
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-screen overflow-hidden m-4">
        <div class="p-4 border-b flex justify-between items-center bg-gray-50">
          <h3 class="text-lg font-semibold">
            <i class="fas fa-list mr-2"></i>
            {{ currentDetailFilter.type === 'shipping' ? 'Shipping' : 'Order' }} Details
            <span v-if="currentSellerName" class="text-gray-500 font-normal">- {{ currentSellerName }}</span>
            <span v-if="currentProductName" class="text-blue-500 font-normal ml-1">({{ currentProductName }})</span>
          </h3>
          <button 
            @click="closeModal"
            :disabled="loadingDetails"
            class="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
        
        <div class="p-4 overflow-y-auto" style="max-height: 70vh;">
          <div v-if="loadingDetails" class="text-center py-10">
            <i class="fas fa-spinner fa-spin fa-2x text-blue-500"></i>
            <p class="mt-3 text-gray-500">Loading data...</p>
          </div>
          
          <template v-else>
            <table v-if="orderDetails.length > 0" class="w-full text-sm">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="px-3 py-2 text-left">Order ID</th>
                  <th class="px-3 py-2 text-left">Date</th>
                  <th class="px-3 py-2 text-left">Customer</th>
                  <th class="px-3 py-2 text-left">Status</th>
                  <th class="px-3 py-2 text-left">TeleConsultant</th>
                  <th v-if="orderDetails[0] && orderDetails[0].order" class="px-3 py-2 text-left">Courier</th>
                  <th class="px-3 py-2 text-left">Products</th>
                  <th class="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody class="divide-y">
                <tr v-for="order in orderDetails" :key="order._id" class="hover:bg-gray-50">
                  <td class="px-3 py-2 font-mono text-xs">
                    {{ order.order ? order.order.id : order.id }}
                    <div v-if="order.trackingNumber" class="text-gray-400">{{ order.trackingNumber }}</div>
                  </td>
                  <td class="px-3 py-2 text-xs">{{ formatDate(order.date) }}</td>
                  <td class="px-3 py-2">
                    <div class="text-xs">
                      <div class="font-medium">{{ order.customer ? order.customer.fullName || "-" : "-" }}</div>
                      <div class="text-gray-400">{{ order.customer ? order.customer.phone || "-" : "-" }}</div>
                    </div>
                  </td>
                  <td class="px-3 py-2">
                    <span 
                      class="px-2 py-1 rounded text-xs font-medium"
                      :class="getStatusBadgeColor(order.status)"
                    >
                      {{ order.status }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-xs">
                    {{ order.order ? order.order.teleConsultantConfirm ? order.order.teleConsultantConfirm.fullName : '-' : order.teleConsultantConfirm ? order.teleConsultantConfirm.fullName || '-' : "-" }}
                  </td>
                  <td v-if="orderDetails[0] && orderDetails[0].order" class="px-3 py-2 text-xs">{{ order.courier ? order.courier.fullName || '-' : "-" }}</td>
                  <td class="px-3 py-2">
                    <div v-for="(prod, idx) in order.products" :key="idx" class="text-xs">
                      {{ prod.productName }} (x{{ prod.quantity }})
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right font-semibold">{{ formatPrice(order.totalPrice) }}</td>
                </tr>
              </tbody>
             
            </table>

            <div v-else class="text-center py-10 text-gray-500">
              No orders found
            </div>

            <div v-if="detailsPagination.pages > 1" class="mt-4 flex justify-center items-center gap-2">
              <button 
                @click="changePage(detailsPagination.page - 1)"
                :disabled="detailsPagination.page <= 1 || loadingDetails"
                class="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              
              <span class="text-sm text-gray-600">
                Page {{ detailsPagination.page }} of {{ detailsPagination.pages }}
                ({{ detailsPagination.total }} total)
              </span>
              
              <button 
                @click="changePage(detailsPagination.page + 1)"
                :disabled="detailsPagination.page >= detailsPagination.pages || loadingDetails"
                class="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SellerAnalytics",
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
    sous_idWharhouse: { type: String },
    sous_warehouses: { type: Array },
  },
  data() {
    return {
      loading: false,
      loadingDetails: false,
      filters: {
        seller: "",
        warehouse: "",
        dateRange: []
      },
      sellers: [],
      warehouses: [],
      analyticsData: null,
      showDetailsModal: false,
      orderDetails: [],
      detailsPagination: { page: 1, pages: 1, total: 0 },
      currentDetailFilter: {},
      currentSellerName: "",
      currentProductId: null,
      currentProductName: ""
    };
  },
  computed: {
    grandTotals() {
      if (!this.analyticsData || !this.analyticsData.sellers) return {};
      const totals = {
        qty: 0, pending: 0, waiting: 0, depot: 0, unreached: 0, expired: 0, confirmed: 0, cancelled: 0,
        queued: 0, prepared: 0, shipped: 0, reprogrammed: 0, refunded: 0, delivered: 0, shipCancelled: 0, totalShipped: 0
      };

      this.analyticsData.sellers.forEach(s => {
        // Confirmation
        totals.qty += s.products.reduce((a, b) => a + b.totalQuantity, 0);
        totals.pending += s.totals.confirmation.pending;
        totals.waiting += s.totals.confirmation.waiting;
        totals.depot += s.totals.confirmation.depot;
        totals.unreached += s.totals.confirmation.unreached;
        totals.expired += s.totals.confirmation.expired;
        totals.confirmed += s.totals.confirmation.confirmed;
        totals.cancelled += s.totals.confirmation.cancelled;
        
       // Shipping
        totals.queued += (s.totals.shipping.queued || 0);
        totals.prepared += (s.totals.shipping.prepared || 0);
        totals.shipped += (s.totals.shipping.shipped || 0);
        totals.reprogrammed += (s.totals.shipping.reprogrammed || 0);
        totals.refunded += (s.totals.shipping.refunded || 0);
        totals.delivered += (s.totals.shipping.delivered || 0);
        totals.shipCancelled += (s.totals.shipping.cancelled || 0);
        totals.totalShipped += (s.totals.shipping.totalShipped || 0);
      });

      return {
        ...totals,
        tauxConf: totals.qty > 0 ? ((totals.confirmed / totals.qty) * 100).toFixed(2) : 0,
        tauxLead: totals.qty > 0 ? ((totals.delivered / totals.qty) * 100).toFixed(2) : 0,
        tauxShip: totals.totalShipped > 0 ? ((totals.delivered / totals.totalShipped) * 100).toFixed(2) : 0
      };
    }
  },
  watch: { 
    'idWharhouse': async function(oldVal, newVal) {
      this.idWharhouse = oldVal;  
      await this.loadInitialData();
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      this.filters.dateRange = [firstDay, now];
    },
  },

  async mounted() {
    await this.loadInitialData();
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    this.filters.dateRange = [firstDay, now];
  },

  methods: {
    getTauxClass(taux) {
      if (taux >= 70) return 'taux-high';
      if (taux >= 40) return 'taux-medium';
      return 'taux-low';
    },
    
    onDateRangeChange(value) {
      this.filters.dateRange = value;
    },
    
    closeModal() {
      if (!this.loadingDetails) {
        this.showDetailsModal = false;
        this.orderDetails = [];
        this.detailsPagination = { page: 1, pages: 1, total: 0 };
      }
    },
    
    async changePage(page) {
      if (this.loadingDetails) return;
      
      if (this.currentDetailFilter.type === 'shipping') {
        await this.fetchShippingDetails(page);
      } else {
        await this.fetchOrderDetails(page);
      }
    },
    
    async loadInitialData() {
      try {
        this.filters.warehouse = this.idWharhouse._id;
        const [sellersRes] = await Promise.all([
          this.$server.search("users", { type: "Seller", warehouses: this.idWharhouse._id, limit: 10 }),
        ]);
        
        if (sellersRes.content.results) {
          this.sellers = sellersRes.content.results;
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    },
    
    getUsersDebance: _.debounce(async function(name) {
      await this.$store.dispatch('wharhouse/searchUser', { type: "Seller", warhouse: this.idWharhouse._id, search: name }).then((res) => {
        this.sellers = res;
      });
    }, 500), 

    async fetchAnalytics() {
      if (!this.filters.warehouse) {
        this.$toast.warning("Please select a warehouse");
        return;
      }

      this.loading = true;
      this.analyticsData = null;
      
      try {
        const payload = {
          warehouse: this.filters.warehouse,
        };

        if (this.filters.seller) {
          payload.seller = this.filters.seller;
        }

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          payload.dateRange = {
            fromDate: this.filters.dateRange[0],
            toDate: this.filters.dateRange[1]
          };
        }

        const res = await this.$server.getSellerAnalytics("orders", payload);

        if (res.success) {
          this.analyticsData = res.data;
        } else {
          this.$toast.error(res.content || "Error fetching analytics");
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
        this.$toast.error("Error fetching analytics");
      } finally {
        this.loading = false;
      }
    },

    async showOrderDetails(sellerId, productId = null, statusFilter = null) {
      const seller = this.analyticsData.sellers.find(s => s.sellerId.toString() === sellerId.toString());
      this.currentSellerName = seller ? seller.sellerName || '' : '';
      // Find product name if productId provided
      if (productId) {
        const products = (seller && seller.products) ? seller.products : [];
        const product = products.find(p =>
          p && p.productId && p.productId.toString() === productId.toString()
        );

        this.currentProductName = product ? product.productName || '' : '';
      } else {
        this.currentProductName = '';
      }

      this.currentDetailFilter = { seller: sellerId, status: statusFilter,product: productId, };
      this.showDetailsModal = true;
      await this.fetchOrderDetails(1);
    },

    async fetchOrderDetails(page = 1) {
      this.loadingDetails = true;
      try {
        const payload = {
          warehouse: this.filters.warehouse,
          page,
          limit: 50
        };

        
        if (this.currentDetailFilter.seller) {
          payload.seller = this.currentDetailFilter.seller;
        }

        if (this.currentDetailFilter.product) {
          payload.product = this.currentDetailFilter.product;
        }

        if (this.currentDetailFilter.status) {
          payload.status = this.currentDetailFilter.status;
        }

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          payload.dateRange = {
            fromDate: this.filters.dateRange[0],
            toDate: this.filters.dateRange[1]
          };
        }

        const res = await this.$server.getSellerOrderDetails("orders", payload);

        if (res.success) {
          this.orderDetails = res.data.orders;
          this.detailsPagination = res.data.pagination;
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        this.loadingDetails = false;
      }
    },

   exportToExcel() {
      if (!this.analyticsData || !this.analyticsData.sellers) return;

      // 1. تحديد العناوين (Headers)
      const headers = [
        "Seller", "Product", "Qty",
        // Confirmation
        "Conf. Pending", "Conf. Waiting", "Conf. Depot", "Conf. Unreached", 
        "Conf. Expired", "Conf. Confirmed", "Conf. Cancelled", "Conf. Taux (Conf/Qty)",
        // Shipping
        "Ship. Queued", "Ship. Prepared", "Ship. Shipped", "Ship. Reprog", 
        "Ship. Refunded", "Ship. Delivered", "Ship. Cancelled", "Ship. Taux (Deliv/Ship)",
        "Ship. Taux/Lead (Deliv/Qty)", "Total Shipped"
      ];

      let csvContent = "\ufeff"; // إضافة BOM لدعم اللغة العربية والرموز
      csvContent += headers.join(";") + "\n"; // استعملنا ";" عوض "," حيت Excel في الغالب كيقراها أحسن

      // 2. إضافة البيانات (Rows)
      this.analyticsData.sellers.forEach(seller => {
        seller.products.forEach(p => {
          const row = [
            seller.sellerName,
            p.productName,
            p.totalQuantity,
            // Confirmation stats
            p.confirmation.pending,
            p.confirmation.waiting,
            p.confirmation.depot,
            p.confirmation.unreached,
            p.confirmation.expired,
            p.confirmation.confirmed,
            p.confirmation.cancelled,
            `${p.confirmation.tauxConfirmation}%`,
            // Shipping stats
            p.shipping.queued,
            p.shipping.prepared,
            p.shipping.shipped,
            p.shipping.reprogrammed,
            p.shipping.refunded,
            p.shipping.delivered,
            p.shipping.cancelled,
            `${p.shipping.tauxLivraison}%`,
            `${p.shipping.tauxPerLead}%`, // العمود الجديد
            p.shipping.totalShipped
          ];
          csvContent += row.join(";") + "\n";
        });

        // سطر المجموع لكل Seller (اختياري، كيعاون في Excel)
        const sellerTotal = [
          `${seller.sellerName} (TOTAL)`, "", seller.products.reduce((sum, pr) => sum + pr.totalQuantity, 0),
          seller.totals.confirmation.pending, seller.totals.confirmation.waiting, seller.totals.confirmation.depot,
          seller.totals.confirmation.unreached, seller.totals.confirmation.expired, seller.totals.confirmation.confirmed,
          seller.totals.confirmation.cancelled, `${seller.totals.confirmation.tauxConfirmation}%`,
          seller.totals.shipping.queued, seller.totals.shipping.prepared, seller.totals.shipping.shipped,
          seller.totals.shipping.reprogrammed, seller.totals.shipping.refunded, seller.totals.shipping.delivered,
          seller.totals.shipping.cancelled, `${seller.totals.shipping.tauxLivraison}%`,
          `${seller.totals.shipping.tauxPerLead}%`, // taux lead hna khaso يتحسب ila bghitih
          seller.totals.shipping.totalShipped
        ];
        csvContent += sellerTotal.join(";") + "\n";
      });

      // 3. إضافة الـ Grand Total في الأخير
      const gt = this.grandTotals; // Computed property اللي صاوبنا قبل
      const grandTotalRow = [
        "GRAND TOTAL", "", gt.qty,
        gt.pending, gt.waiting, gt.depot, gt.unreached, gt.expired, gt.confirmed, gt.cancelled, `${gt.tauxConf}%`,
        gt.queued, gt.prepared, gt.shipped, gt.reprogrammed, gt.refunded, gt.delivered, gt.shipCancelled, `${gt.tauxShip}%`,
        `${gt.tauxLead}%`, gt.totalShipped
      ];
      csvContent += grandTotalRow.join(";") + "\n";

      // 4. إنشاء الملف وتحميله
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      const dateStr = new Date().toISOString().slice(0, 10);
      link.setAttribute("href", url);
      link.setAttribute("download", `Analytics_Sellers_${dateStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    formatPrice(price) {
      return new Intl.NumberFormat(`fr-${this.idWharhouse.country.toUpperCase()}`, { 
        style: 'currency', 
        currency: this.idWharhouse.currency,
        minimumFractionDigits: 0
      }).format(price || 0);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    },

    getStatusBadgeColor(status) {
      const s = status.toLowerCase();
      if (['confirmed', 'paid', 'received'].includes(s)) return 'bg-green-100 text-green-800';
      if (['cancelled', 'refunded'].includes(s)) return 'bg-red-100 text-red-800';
      if (['pending', 'waiting', 'queued'].includes(s)) return 'bg-yellow-100 text-yellow-800';
      if (['shipped', 'prepared'].includes(s)) return 'bg-blue-100 text-blue-800';
      return 'bg-gray-100 text-gray-800';
    },
    
    async showShippingDetails(sellerId, productId = null, statusFilter = null) {
      const seller = this.analyticsData.sellers.find(s => s.sellerId.toString() === sellerId.toString());
      this.currentSellerName = seller ? seller.sellerName || '': '';

      console.log('productId showShippingDetails:', productId);
      console.log('seller showShippingDetails:', seller);

      if (productId) {
        const products = (seller && seller.products) ? seller.products : [];
        const product = products.find(p =>
          p && p.productId && p.productId.toString() === productId.toString()
        );
        this.currentProductName = product ? product.productName || '' : '';
      } else {
        this.currentProductName = '';
      }

      this.currentDetailFilter = { seller: sellerId,product: productId, type: 'shipping',status: statusFilter, };
      this.showDetailsModal = true;
      await this.fetchShippingDetails(1);
    },

    async fetchShippingDetails(page = 1) {
      this.loadingDetails = true;
      try {
        const payload = {
          warehouse: this.filters.warehouse,
          page,
          limit: 50
        };

        if (this.currentDetailFilter.seller) {
          payload.seller = this.currentDetailFilter.seller;
        }

        if (this.currentDetailFilter.product) {
          payload.product = this.currentDetailFilter.product;
        }


        if (this.currentDetailFilter.status) {
          payload.status = this.currentDetailFilter.status;
        }

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          payload.dateRange = {
            fromDate: this.filters.dateRange[0],
            toDate: this.filters.dateRange[1]
          };
        }

        const res = await this.$server.getSellerShippingDetails("orders", payload);

        if (res.success) {
          this.orderDetails = res.data.shippings;
          this.detailsPagination = res.data.pagination;
        }
      } catch (error) {
        console.error("Error fetching shipping details:", error);
      } finally {
        this.loadingDetails = false;
      }
    },
  }
};
</script>

<style scoped>
.seller-analytics table th,
.seller-analytics table td {
  white-space: nowrap;
}

.seller-analytics table {
  border-collapse: separate;
  border-spacing: 0;
}

.seller-analytics table th {
  position: sticky;
  top: 0;
  z-index: 10;
}

.seller-analytics tbody tr:nth-child(even) {
  /* background-color: #f8fafc; */
}

.seller-analytics tbody tr:hover {
  /* background-color: #e0f2fe !important; */
}

.section-confirmation {
  border-left: 3px solid #3b82f6 !important;
}

.section-shipping {
  border-left: 3px solid #10b981 !important;
}

.cell-pending {
  background-color: #fef9c3 !important;
  color: #854d0e;
  font-weight: 600;
}

.cell-waiting {
  background-color: #ffedd5 !important;
  color: #9a3412;
}

.cell-confirmed {
  background-color: #dcfce7 !important;
  color: #166534;
  font-weight: 700;
}

.cell-cancelled {
  background-color: #fee2e2 !important;
  color: #991b1b;
  font-weight: 600;
}

.cell-shipped {
  background-color: #dbeafe !important;
  color: #1e40af;
}

.cell-delivered {
  background-color: #d1fae5 !important;
  color: #065f46;
  font-weight: 700;
}

.cell-taux {
  background-color: #f3e8ff !important;
  color: #6b21a8;
  font-weight: 800;
  font-size: 0.875rem;
}

.header-main {
  background-color: #1e293b !important;
  color: white !important;
  font-weight: 700;
  padding: 12px 16px !important;
}

.header-confirmation {
  background-color: #1d4ed8 !important;
  color: white !important;
  font-weight: 700;
}

.header-shipping {
  background-color: #059669 !important;
  color: white !important;
  font-weight: 700;
}

.header-sub-conf {
  background-color: #dbeafe !important;
  color: #1e40af !important;
  font-weight: 600;
  font-size: 0.7rem;
  padding: 8px 6px !important;
}

.header-sub-ship {
  background-color: #d1fae5 !important;
  color: #065f46 !important;
  font-weight: 600;
  font-size: 0.7rem;
  padding: 8px 6px !important;
}

.value-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.8rem;
}

.value-zero {
  color: #9ca3af;
}

.taux-high {
  background-color: #dcfce7;
  color: #166534;
}

.taux-medium {
  background-color: #fef9c3;
  color: #854d0e;
}

.taux-low {
  background-color: #fee2e2;
  color: #991b1b;
}

.summaryseller,.summaryseller:hover{
  background-color: #3182ce;
}
/* Clickable Stats Badge */
.stat-clickable {
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 2px dashed transparent;
}

.stat-clickable:hover {
  transform: scale(1.1);
  border-bottom-color: currentColor;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.stat-clickable::after {
  content: '';
  position: absolute;
  top: -2px;
  right: -2px;
  width: 6px;
  height: 6px;
  background-color: #3b82f6;
  border-radius: 50%;
  opacity: 0.7;
}

/* Variant for non-zero values only */
.stat-clickable.has-value::after {
  background-color: #10b981;
}

.stat-clickable:hover::after {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.3); opacity: 1; }
}

</style>